import { apiRoot } from '@/lib/tenantHost'

/**
 * What the API says about the custom domain this page is on. Only a 404 means
 * the domain isn't a workspace; anything else that fails (network down, 5xx,
 * a gateway error) is `unreachable`, which the user can retry. Telling every
 * visitor "Workspace not found" during an outage would be wrong and alarming.
 */
export type CustomDomainLookupResult =
  | { status: 'found'; slug: string }
  | { status: 'not-found' }
  | { status: 'unreachable' }

/** Wait before the one automatic retry: long enough to ride out a deploy or a blip. */
export const LOOKUP_RETRY_DELAY_MS = 1500

interface LookupOptions {
  fetchImpl?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  retryDelayMs?: number
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

async function attempt(
  hostname: string,
  fetchImpl: typeof fetch
): Promise<CustomDomainLookupResult> {
  try {
    const response = await fetchImpl(
      `${apiRoot()}/v1/public/tenants/host/${encodeURIComponent(hostname)}`,
      { credentials: 'include' }
    )
    if (response.status === 404) return { status: 'not-found' }
    if (!response.ok) return { status: 'unreachable' }
    const body = (await response.json()) as { data?: { slug?: string } }
    return body.data?.slug ? { status: 'found', slug: body.data.slug } : { status: 'unreachable' }
  } catch {
    return { status: 'unreachable' }
  }
}

/** Which workspace `hostname` serves, retrying once (after a backoff) if the API can't be reached. */
export async function lookupCustomDomain(
  hostname: string,
  { fetchImpl = fetch, sleep = wait, retryDelayMs = LOOKUP_RETRY_DELAY_MS }: LookupOptions = {}
): Promise<CustomDomainLookupResult> {
  const first = await attempt(hostname, fetchImpl)
  if (first.status !== 'unreachable') return first
  await sleep(retryDelayMs)
  return attempt(hostname, fetchImpl)
}
